import { FORCE_VARIANT_PARAM } from 'constants/personalize';

import { HttpClient } from 'services/http';
import { Logger } from 'services/logger';

import { PAGE_INCLUDES, buildIncludeQuery } from './includes';

const API_KEY = process.env.CONTENTSTACK_API_KEY;
const DELIVERY_TOKEN = process.env.CONTENTSTACK_DELIVERY_TOKEN;
const ENVIRONMENT = process.env.CONTENTSTACK_ENVIRONMENT;
const CDN_HOST = process.env.CONTENTSTACK_CDN_HOST;
const INCLUDE_QUERY = buildIncludeQuery(PAGE_INCLUDES);

enum EarlyFetchStatus {
  NotRun = 'not-run',
  NoVariants = 'no-variants',
  Success = 'success',
}

type EarlyFetchResult =
  | { status: EarlyFetchStatus.NotRun }
  | { status: EarlyFetchStatus.NoVariants }
  | { status: EarlyFetchStatus.Success; entry: Record<string, unknown> };

const cache = new Map<string, Record<string, unknown> | null>();
const cacheKey = (uid: string, aliases: string[]): string => `${uid}::${[...aliases].sort().join(',')}`;

export class PersonalizeCSRFetchService {
  static async fetchPersonalizedPage(uid: string, aliases: string[]): Promise<Record<string, unknown> | null> {
    const key = cacheKey(uid, aliases);

    if (cache.has(key)) return cache.get(key)!;

    const earlyResult = await PersonalizeCSRFetchService.consumeEarlyFetch(uid);

    if (earlyResult.status === EarlyFetchStatus.NoVariants) return null;

    if (earlyResult.status === EarlyFetchStatus.Success) {
      cache.set(key, earlyResult.entry);
      return earlyResult.entry;
    }

    // status === 'not-run': early fetch didn't execute (SPA nav, ?variant= override, missing promise)
    const entry = await PersonalizeCSRFetchService.fetch(uid, aliases);
    cache.set(key, entry);
    return entry;
  }

  // Early fetch kicks off in <head> before React hydrates, parking the result in
  // window.__personalizePagePromise. On SPA navigation the head script doesn't re-run,
  // so the promise is absent and we fall back to a fresh fetch.
  // ?variant= override skips early fetch because it used the original (non-overridden) aliases.
  private static async consumeEarlyFetch(uid: string): Promise<EarlyFetchResult> {
    const hasVariantOverride = new URLSearchParams(window.location.search).has(FORCE_VARIANT_PARAM);
    if (hasVariantOverride || !window.__personalizePagePromise || window.__pageUid !== uid) {
      return { status: EarlyFetchStatus.NotRun };
    }

    const entry = await window.__personalizePagePromise;
    window.__personalizePagePromise = null;
    return entry ? { status: EarlyFetchStatus.Success, entry } : { status: EarlyFetchStatus.NoVariants };
  }

  private static async fetch(uid: string, aliases: string[]): Promise<Record<string, unknown> | null> {
    const url = `https://${CDN_HOST}/v3/content_types/_page_page/entries/${uid}?${INCLUDE_QUERY}&environment=${ENVIRONMENT}&show_errors=true`;
    const aliasHeader = aliases.join(',');

    try {
      const response = await HttpClient.get(url, {
        headers: {
          api_key: API_KEY ?? '',
          access_token: DELIVERY_TOKEN ?? '',
          'x-cs-variant-uid': aliasHeader,
        },
      });

      const data = await response.json();
      const entry = data?.entry;

      return entry?.publish_details?.variants ? entry : null;
    } catch (err) {
      Logger.warn(`[PersonalizeCSR] Fetch error for uid=${uid} aliases=${aliasHeader}:`, err);
      return null;
    }
  }
}
